import { state } from '../shared/state';
import { $, showToast } from '../shared/index';

/**
 * Диктор Silero — свободная строка: набор зависит от загруженных сайдкаром
 * пакетов модели (сейчас 34 диктора двух пакетов), каталог приходит из
 * GET /api/admin/avg-dialog/voices. Раньше здесь был закрытый союз из пяти
 * дикторов v5_5_ru, и любое обновление модели ломало типизацию формы.
 */
type SileroSpeaker = string;

type SileroVoice = {
  id: string;
  package?: string;
  license?: string;
  gender?: 'male' | 'female' | 'unknown';
};
type SoundProfile = 'professional' | 'warm' | 'neutral' | 'energetic';
type VideoResolution = '480p' | '720p';
type LocalVideoPolicy = 'video_first' | 'audio_first';
type AnswerLength = 'short' | 'medium' | 'full';
type DialogMode = 'live' | 'scripted';
type CaptionsMode = 'immediate' | 'after_playback' | 'off';

interface AvatarProfile {
  avatar_id: string;
  label?: string;
  category?: string | null;
  /** Порядок в списках выбора: по возрастанию, при равенстве — по имени. */
  sort_index?: number;
  silero_speaker?: SileroSpeaker;
  sound_profile?: SoundProfile;
  speech_rate?: number;
  pitch?: number | null;
  /** Видео персоны — реквизит внешности, заменивший «Изображение лица». */
  profile_video_path?: string | null;
  /** Легаси-фото: в форме больше не редактируется, остаётся фоллбэком. */
  face_image_path?: string | null;
  /** Заставка — статичная подложка окна видео Диалога (png/jpeg/webp). */
  splash_image_path?: string | null;
  base_path?: string | null;
  idle_replica?: string | null;
  idle_animation?: string | null;
  video_resolution?: VideoResolution;
  captions_enabled?: boolean;
  /** Приоритетнее captions_enabled; NULL — сервер мапит из него (см. миграцию
   * avg-dialog/007_captions_mode). */
  captions_mode?: CaptionsMode | null;
  greeting?: string | null;
  /** Привязка личности: паспорт и его версия (ADR-008 §3.4). */
  passport_id?: string | null;
  passport_version?: string | null;
  /** Методика работы (A3) и контракт результата (A6) — не личность, свои версии. */
  method_frame_id?: string | null;
  method_frame_version?: number | null;
  output_frame_id?: string | null;
  output_frame_version?: number | null;
  local_video_policy?: LocalVideoPolicy;
  video_ready_timeout_ms?: number | null;
  barge_in_enabled?: boolean;
  answer_length?: AnswerLength;
  max_turns?: number | null;
  timeout_min?: number | null;
  idle_clips_dir?: string | null;
  lipsync_engine?: 'wav2lip' | 'kling';
  yandex_voice?: string | null;
  /** Амплуа голоса Yandex (role в TTS v3); допустимые пары — YANDEX_RU_VOICES. */
  yandex_role?: string | null;
  /** Смещение тембра Yandex в Гц (hints.pitchShift в TTS v3, -1000..1000). */
  yandex_pitch_shift?: number | null;
  tts_provider?: 'silero' | 'yandex' | 'silero_api' | null;
  silero_api_voice?: string | null;
  silero_api_emotion?: string | null;
  stt_provider?: 'browser' | 'whisper' | 'silero_api' | 'yandex' | null;
  stt_enhance?: boolean;
}

/**
 * Состояние личности одной записи каталога (GET /avg-dialog/personality).
 * Сервер отвечает не «заполнено ли поле», а «разрешится ли личность на ходе»:
 * между этими двумя ответами и живёт Аватар, молча говорящий промптом
 * категории.
 */
interface PersonalityStatus {
  avatar_id: string;
  label?: string;
  status: 'resolved' | 'no_passport' | 'passport_missing' | 'passport_broken';
  passport_id?: string | null;
  passport_version?: string | null;
  er_type_id?: string;
  er_type_version?: string;
  detail?: string | null;
}

/** Что вообще можно привязать: загруженные паспорта и рамки. */
interface PersonalityRefs {
  passports: Array<{ passportId: string; version: string; label?: string; erTypeId?: string; status?: string }>;
  method_frames: Array<{ frame_id: string; version: number; label?: string }>;
  output_frames: Array<{ frame_id: string; version: number; label?: string }>;
}

interface DialogSettings {
  default_avatar_id?: string;
  mode?: DialogMode;
  max_turns?: number;
  timeout_min?: number;
  stt_provider?: string | null;
  voice_barge_in_enabled?: boolean;
  history_messages?: number;
  history_summary_enabled?: boolean;
  tts_accents?: string;
  stt_enhance?: boolean;
}

/**
 * Русские голоса Yandex SpeechKit и их амплуа. Зеркало бэкенд-каталога
 * src/modules/avg-dialog/yandex-voices.ts (webui собирается отдельным
 * TS-графом и бэкенд-модули не импортирует) — правки вносить в оба.
 * Первый элемент roles — дефолтное амплуа голоса по документации.
 */
const YANDEX_RU_VOICES: Array<{ id: string; gender: 'М' | 'Ж'; roles: string[]; legacy?: boolean }> = [
  { id: 'alena', gender: 'Ж', roles: ['neutral', 'good'], legacy: true },
  { id: 'marina', gender: 'Ж', roles: ['neutral', 'whisper', 'friendly'] },
  { id: 'jane', gender: 'Ж', roles: ['neutral', 'good', 'evil'] },
  { id: 'omazh', gender: 'Ж', roles: ['neutral', 'evil'] },
  { id: 'dasha', gender: 'Ж', roles: ['neutral', 'good', 'friendly'] },
  { id: 'julia', gender: 'Ж', roles: ['neutral', 'strict'] },
  { id: 'lera', gender: 'Ж', roles: ['neutral', 'friendly'] },
  { id: 'masha', gender: 'Ж', roles: ['good', 'strict', 'friendly'] },
  { id: 'saule_ru', gender: 'Ж', roles: ['neutral', 'strict', 'whisper'] },
  { id: 'zamira_ru', gender: 'Ж', roles: ['neutral', 'strict', 'friendly'] },
  { id: 'zhanar_ru', gender: 'Ж', roles: ['neutral', 'strict', 'friendly'] },
  { id: 'yulduz_ru', gender: 'Ж', roles: ['neutral', 'strict', 'friendly', 'whisper'] },
  { id: 'filipp', gender: 'М', roles: [] },
  { id: 'ermil', gender: 'М', roles: ['neutral', 'good'] },
  { id: 'zahar', gender: 'М', roles: ['neutral', 'good'] },
  { id: 'alexander', gender: 'М', roles: ['neutral', 'good'] },
  { id: 'kirill', gender: 'М', roles: ['neutral', 'strict', 'good'] },
  { id: 'anton', gender: 'М', roles: ['neutral', 'good'] },
  { id: 'madi_ru', gender: 'М', roles: [] },
];

const YANDEX_ROLE_LABELS: Record<string, string> = {
  neutral: 'нейтральное',
  good: 'радостное',
  evil: 'раздражённое',
  strict: 'строгое',
  friendly: 'дружелюбное',
  whisper: 'шёпот',
};

/**
 * Каталог дикторов локального Silero. Заполняется при открытии раздела из
 * GET /api/admin/avg-dialog/voices (проксирует /speakers сайдкара); до ответа
 * и при недоступном сайдкаре работает этот минимальный список — он совпадает
 * с фоллбэком сервера и с дефолтами пресетов.
 */
let sileroVoicesCache: SileroVoice[] = [
  { id: 'ru_zhadyra', package: 'v5_cis_base_nostress', license: 'MIT', gender: 'female' },
  { id: 'ru_zinaida', package: 'v5_cis_base_nostress', license: 'MIT', gender: 'female' },
  { id: 'ru_vika', package: 'v5_cis_base_nostress', license: 'MIT', gender: 'female' },
  { id: 'ru_dmitriy', package: 'v5_cis_base_nostress', license: 'MIT', gender: 'male' },
  { id: 'ru_igor', package: 'v5_cis_base_nostress', license: 'MIT', gender: 'male' },
];

const SILERO_DEFAULT_SPEAKER = 'ru_zhadyra';

async function loadSileroVoices(): Promise<void> {
  const data = await fetchJson<{ voices?: SileroVoice[] }>('/api/admin/avg-dialog/voices', {
    headers: authHeaders(),
  });
  if (data?.voices?.length) sileroVoicesCache = data.voices;
}

/**
 * Заполняет селект дикторов Silero: две группы по полу, в подписи — лицензия
 * пакета (MIT — можно в коммерческом продукте, CC-BY-NC — нет).
 */
function populateSileroSpeakerSelect(selected: string): void {
  const select = $<HTMLSelectElement>('admin-adlg-speaker');
  if (!select) return;
  const groups: Array<[string, SileroVoice['gender']]> = [
    ['Женские', 'female'],
    ['Мужские', 'male'],
    ['Пол не определён', 'unknown'],
  ];
  const html: string[] = [];
  for (const [title, gender] of groups) {
    const voices = sileroVoicesCache.filter((v) => (v.gender || 'unknown') === gender);
    if (!voices.length) continue;
    html.push(`<optgroup label="${title}">`);
    for (const v of voices) {
      const licence = v.license ? ` — ${escapeHtml(v.license)}` : '';
      html.push(`<option value="${escapeHtml(v.id)}">${escapeHtml(v.id)}${licence}</option>`);
    }
    html.push('</optgroup>');
  }
  select.innerHTML = html.join('');
  // Сохранённый в профиле диктор мог исчезнуть вместе с пакетом модели —
  // не теряем значение молча, показываем его отдельным пунктом.
  if (selected && !sileroVoicesCache.some((v) => v.id === selected)) {
    select.insertAdjacentHTML(
      'beforeend',
      `<option value="${escapeHtml(selected)}">${escapeHtml(selected)} — нет в загруженных пакетах</option>`
    );
  }
  select.value = selected || SILERO_DEFAULT_SPEAKER;
}

/**
 * Проба голоса: синтезирует фразу теми настройками, которые сейчас стоят в
 * форме, — до сохранения профиля. Провайдер берётся из формы и НЕ подменяется:
 * сервер отвечает ошибкой, если выбранное облако не настроено или не ответило
 * (в бою там каскад на локальный Silero, и проверка бы врала).
 */
async function previewVoice(): Promise<void> {
  const button = $<HTMLButtonElement>('btn-admin-adlg-voice-preview');
  const status = $('admin-adlg-voice-preview-status');
  const audio = $<HTMLAudioElement>('admin-adlg-voice-preview-audio');
  if (!button || !status || !audio) return;

  const provider = $<HTMLSelectElement>('admin-adlg-tts-provider')?.value || 'silero';
  const payload = {
    provider,
    silero_speaker: $<HTMLSelectElement>('admin-adlg-speaker')?.value || null,
    sound_profile: $<HTMLSelectElement>('admin-adlg-sound-profile')?.value || null,
    speech_rate: Number($<HTMLInputElement>('admin-adlg-speech-rate')?.value || '1'),
    yandex_voice: $<HTMLSelectElement>('admin-adlg-yandex-voice')?.value || null,
    yandex_role: $<HTMLSelectElement>('admin-adlg-yandex-role')?.value || null,
    yandex_pitch_shift: readNumberOrUndefined('admin-adlg-yandex-pitch-shift') ?? null,
    silero_api_voice: $<HTMLInputElement>('admin-adlg-silero-api-voice')?.value.trim() || null,
    silero_api_emotion: $<HTMLInputElement>('admin-adlg-silero-api-emotion')?.value.trim() || null,
    text: $<HTMLInputElement>('admin-adlg-voice-preview-text')?.value.trim() || null,
  };

  button.disabled = true;
  status.textContent = 'Синтезирую…';
  audio.classList.add('hidden');
  try {
    const r = await fetch('/api/admin/avg-dialog/voice-preview', {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(payload),
    });
    const data = await r.json().catch(() => null) as
      | { ok?: boolean; detail?: string; audio_url?: string; provider?: string; speaker?: string }
      | null;
    if (!r.ok || !data) {
      status.textContent = `❌ ${data?.detail || `Сервер ответил ${r.status}`}`;
      return;
    }
    if (!data.ok || !data.audio_url) {
      status.textContent = `❌ ${data.detail || 'Синтез не удался'}`;
      return;
    }
    // Кэш проб живёт по хэшу настроек, поэтому одна и та же проба переиспользует
    // файл; ?t= нужен только чтобы браузер не показал прошлый звук при повторе.
    audio.src = `${data.audio_url}?t=${Date.now()}`;
    audio.classList.remove('hidden');
    status.textContent = `✅ ${data.provider || provider}${data.speaker ? ` · ${data.speaker}` : ''}`;
    void audio.play().catch(() => {
      /* автовоспроизведение может быть запрещено — плеер уже виден */
    });
  } catch (err) {
    status.textContent = `❌ ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    button.disabled = false;
  }
}

/** Заполняет селект голосов Yandex из каталога (однократно на открытие формы). */
function populateYandexVoiceSelect(selected: string): void {
  const select = $<HTMLSelectElement>('admin-adlg-yandex-voice');
  if (!select) return;
  const options = ['<option value="">по умолчанию (alena)</option>'];
  for (const v of YANDEX_RU_VOICES) {
    const suffix = (v.gender === 'Ж' ? ' — женский' : ' — мужской') + (v.legacy ? ' (легаси)' : '');
    options.push(`<option value="${v.id}">${v.id}${suffix}</option>`);
  }
  select.innerHTML = options.join('');
  // Неизвестное сохранённое значение не теряем — показываем как есть.
  if (selected && !YANDEX_RU_VOICES.some((v) => v.id === selected)) {
    select.insertAdjacentHTML('beforeend', `<option value="${escapeHtml(selected)}">${escapeHtml(selected)}</option>`);
  }
  select.value = selected;
}

/**
 * Перестраивает селект амплуа под выбранный голос: у каждого голоса свой набор
 * (несовместимая пара — ошибка сервиса Yandex). Прежний выбор сохраняется,
 * если он допустим для нового голоса.
 */
function syncYandexRoleOptions(selectedRole?: string): void {
  const roleSelect = $<HTMLSelectElement>('admin-adlg-yandex-role');
  if (!roleSelect) return;
  const voiceId = $<HTMLSelectElement>('admin-adlg-yandex-voice')?.value || 'alena';
  const roles = YANDEX_RU_VOICES.find((v) => v.id === voiceId)?.roles ?? [];
  const keep = selectedRole !== undefined ? selectedRole : roleSelect.value;
  const options = ['<option value="">по умолчанию</option>'];
  for (const role of roles) {
    options.push(`<option value="${role}">${YANDEX_ROLE_LABELS[role] || role} — ${role}</option>`);
  }
  roleSelect.innerHTML = options.join('');
  roleSelect.disabled = roles.length === 0;
  roleSelect.value = roles.includes(keep) ? keep : '';
}

let profilesCache: AvatarProfile[] = [];
let categoriesCache: string[] = [];
let personalityCache: PersonalityStatus[] = [];
let personalityRefsCache: PersonalityRefs = { passports: [], method_frames: [], output_frames: [] };
let editingAvatarId: string | null = null;
let eventsBound = false;

function escapeHtml(text: string): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Converts a stored media path to a browsable URL. The DB stores the on-disk
 * path (`data/media/profile-video/x.mp4`); the server serves it under
 * `/media/...`. Absolute URLs and already-public paths pass through unchanged.
 */
function mediaUrl(p?: string | null): string {
  if (!p) return '';
  if (/^https?:\/\//.test(p) || p.startsWith('/')) return p;
  if (p.startsWith('data/media/')) return '/' + p.slice('data/'.length);
  return p;
}

/** Показывает/прячет предпросмотр видео профиля и кнопку «Убрать». */
function setProfileVideoPreview(url: string): void {
  const preview = $<HTMLVideoElement>('admin-adlg-profile-video-preview');
  const clearBtn = $('btn-admin-adlg-profile-video-clear');
  if (preview) {
    if (url) {
      preview.src = url;
      preview.classList.remove('hidden');
      void preview.play().catch(() => undefined);
    } else {
      preview.pause();
      preview.removeAttribute('src');
      preview.load();
      preview.classList.add('hidden');
    }
  }
  clearBtn?.classList.toggle('hidden', !url);
}

async function fetchAdminCategories(): Promise<string[]> {
  const data = await fetchJson<Record<string, unknown>>('/api/admin/categories', { headers: authHeaders() });
  return data ? Object.keys(data).sort() : [];
}

/** Options for the per-profile dialog-category selector. Includes the profile's
 * own saved category even if it is missing from the current category list. */
function categoryOptionsHtml(selected = ''): string {
  const names = [...categoriesCache];
  if (selected && !names.includes(selected)) names.push(selected);
  const opts = names
    .map((name) => `<option value="${escapeHtml(name)}"${name === selected ? ' selected' : ''}>${escapeHtml(name)}</option>`)
    .join('');
  return `<option value="">— не задана —</option>${opts}`;
}

function populateCategorySelect(selected = ''): void {
  const sel = $<HTMLSelectElement>('admin-adlg-category');
  if (sel) sel.innerHTML = categoryOptionsHtml(selected);
}

function authHeaders(json = false): Record<string, string> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + state.authToken };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  try {
    const r = await fetch(url, init);
    if (!r.ok) return null;
    return await r.json() as T;
  } catch {
    return null;
  }
}

function profileOptionsHtml(selected = ''): string {
  const opts = profilesCache.map((p) =>
    `<option value="${escapeHtml(p.avatar_id)}"${p.avatar_id === selected ? ' selected' : ''}>${escapeHtml(p.label || p.avatar_id)}</option>`
  ).join('');
  return `<option value="">— не назначено —</option>${opts}`;
}

/** Человеческие имена шести Типов — те же, что в шаблонах Базы знаний. */
const ER_TYPE_LABELS: Record<string, string> = {
  narod: 'Народ',
  hranitel: 'Хранитель',
  warrior: 'Воин',
  decoder: 'Декодер',
  nespiaschiy: 'Неспящий',
  shaman: 'Шаман',
};

/**
 * Подсказки формы привязки: паспорта и рамки, которые реально лежат в
 * хранилище. Поля остаются текстовыми (версия — часть идентичности, а не
 * выбор из двух), но набирать имя вслепую больше не нужно: опечатка в версии
 * и есть тот самый «паспорт привязан, а личности нет».
 */
function renderPersonalityRefs(): void {
  const fill = (id: string, options: string[]): void => {
    const el = $<HTMLDataListElement>(id);
    if (!el) return;
    el.innerHTML = options.map((v) => `<option value="${escapeHtml(v)}"></option>`).join('');
  };
  fill('admin-adlg-passport-ids', [...new Set(personalityRefsCache.passports.map((p) => p.passportId))]);
  fill('admin-adlg-passport-versions', [...new Set(personalityRefsCache.passports.map((p) => p.version))]);
  fill('admin-adlg-method-frame-ids', [...new Set(personalityRefsCache.method_frames.map((f) => f.frame_id))]);
  fill('admin-adlg-output-frame-ids', [...new Set(personalityRefsCache.output_frames.map((f) => f.frame_id))]);
}

/**
 * Состояние личности в самой форме — не «что записано в полях», а «что
 * получится на ходе». Разница видна только так: заполненные поля с опечаткой
 * выглядят как привязанная личность, а Аватар при этом отвечает промптом
 * категории.
 */
function renderPersonalityState(avatarId: string | null): void {
  const el = $('admin-adlg-personality-state');
  if (!el) return;
  if (!avatarId) {
    el.textContent = 'Новый Аватар заводится с паспортом: Аватар без личности существовать не может.';
    return;
  }
  const item = personalityCache.find((p) => p.avatar_id === avatarId);
  if (!item) {
    el.textContent = '';
    return;
  }
  if (item.status === 'resolved') {
    const type = ER_TYPE_LABELS[item.er_type_id || ''] || item.er_type_id || '';
    el.textContent =
      `✅ Личность применяется: паспорт ${item.passport_id}@${item.passport_version}, Тип «${type}».`;
    return;
  }
  el.textContent = `⚠️ ${item.detail || 'Личность не применяется'}`;
}

/** Строка о личности в карточке профиля: применяется, не привязана или сломана. */
function personalityBadge(avatarId: string): string {
  const item = personalityCache.find((p) => p.avatar_id === avatarId);
  if (!item) return '';
  if (item.status === 'resolved') {
    const type = ER_TYPE_LABELS[item.er_type_id || ''] || item.er_type_id || '';
    return ` · Личность: ${escapeHtml(item.passport_id || '')}@${escapeHtml(item.passport_version || '')} (${escapeHtml(type)})`;
  }
  if (item.status === 'no_passport') return ' · ⚠️ личность не привязана';
  return ' · ⛔ личность не применяется';
}

function renderProfileList(): void {
  const list = $('admin-adlg-profile-list');
  if (!list) return;
  list.textContent = '';

  if (!profilesCache.length) {
    list.innerHTML = '<div class="audit-log-empty">Аватары пока не созданы.</div>';
  } else {
    profilesCache.forEach((p) => {
      const el = document.createElement('div');
      el.className = 'user-item';
      const voice = p.silero_speaker || '—';
      const sound = p.sound_profile || '—';
      const res = p.video_resolution || '480p';
      el.innerHTML = `
        <div class="user-item-info">
          <span class="user-item-name">${escapeHtml(p.label || p.avatar_id)}</span>
          <span class="user-item-cat">ID: ${escapeHtml(p.avatar_id)} · Голос: ${escapeHtml(voice)} · Звучание: ${escapeHtml(sound)} · ${escapeHtml(res)}${personalityBadge(p.avatar_id)}</span>
        </div>
        <div class="user-item-actions">
          <button class="nav-btn btn-action-sm admin-adlg-edit-btn" data-avatar-id="${escapeHtml(p.avatar_id)}" aria-label="Редактировать ${escapeHtml(p.avatar_id)}">✏️</button>
          <button class="nav-btn btn-action-sm admin-adlg-delete-btn" data-avatar-id="${escapeHtml(p.avatar_id)}" aria-label="Удалить ${escapeHtml(p.avatar_id)}">🗑️</button>
        </div>
      `;
      list.appendChild(el);
    });
  }

  list.querySelectorAll<HTMLButtonElement>('.admin-adlg-edit-btn').forEach((btn) => {
    btn.addEventListener('click', () => void openEditor(btn.dataset.avatarId || null));
  });
  list.querySelectorAll<HTMLButtonElement>('.admin-adlg-delete-btn').forEach((btn) => {
    btn.addEventListener('click', () => void deleteProfile(btn.dataset.avatarId || ''));
  });

  const summary = $('admin-adlg-summary');
  if (summary) {
    // Аватаров без личности быть не должно (ADR-008 §8) — счётчик показывает,
    // сколько записей каталога ещё отвечают промптом категории.
    const without = personalityCache.filter((p) => p.status !== 'resolved').length;
    summary.textContent = without
      ? `${profilesCache.length} профилей аватаров · без личности: ${without}`
      : `${profilesCache.length} профилей аватаров`;
  }

  const defaultSel = $<HTMLSelectElement>('admin-adlg-settings-default-avatar');
  if (defaultSel) {
    const prev = defaultSel.value;
    defaultSel.innerHTML = profileOptionsHtml(prev);
  }
}

function showEditor(show: boolean): void {
  $('admin-adlg-editor')?.classList.toggle('hidden', !show);
  $('admin-adlg-list-panel')?.classList.toggle('hidden', show);
}

function resetEditor(): void {
  editingAvatarId = null;
  $<HTMLInputElement>('admin-adlg-avatar-id')!.value = '';
  $<HTMLInputElement>('admin-adlg-avatar-id')!.disabled = false;
  $<HTMLInputElement>('admin-adlg-label')!.value = '';
  $<HTMLInputElement>('admin-adlg-sort-index')!.value = '0';
  populateCategorySelect('');
  populateSileroSpeakerSelect('');
  $<HTMLSelectElement>('admin-adlg-sound-profile')!.value = 'neutral';
  $<HTMLInputElement>('admin-adlg-speech-rate')!.value = '1';
  updateSpeechRateLabel();
  $<HTMLInputElement>('admin-adlg-profile-video-file')!.value = '';
  $<HTMLInputElement>('admin-adlg-profile-video-path')!.value = '';
  setProfileVideoPreview('');
  $<HTMLInputElement>('admin-adlg-splash-file')!.value = '';
  $<HTMLInputElement>('admin-adlg-splash-path')!.value = '';
  setSplashPreview('');
  $<HTMLSelectElement>('admin-adlg-video-resolution')!.value = '480p';
  $<HTMLSelectElement>('admin-adlg-captions-mode')!.value = 'immediate';
  $<HTMLTextAreaElement>('admin-adlg-greeting')!.value = '';
  $<HTMLInputElement>('admin-adlg-passport-id')!.value = '';
  $<HTMLInputElement>('admin-adlg-passport-version')!.value = '';
  $<HTMLInputElement>('admin-adlg-method-frame-id')!.value = '';
  $<HTMLInputElement>('admin-adlg-method-frame-version')!.value = '';
  $<HTMLInputElement>('admin-adlg-output-frame-id')!.value = '';
  $<HTMLInputElement>('admin-adlg-output-frame-version')!.value = '';
  $<HTMLSelectElement>('admin-adlg-local-video-policy')!.value = 'video_first';
  $<HTMLInputElement>('admin-adlg-video-ready-timeout')!.value = '';
  $<HTMLInputElement>('admin-adlg-barge-in-enabled')!.checked = false;
  $<HTMLSelectElement>('admin-adlg-answer-length')!.value = 'medium';
  $<HTMLSelectElement>('admin-adlg-lipsync-engine')!.value = 'wav2lip';
  populateYandexVoiceSelect('');
  syncYandexRoleOptions('');
  $<HTMLInputElement>('admin-adlg-yandex-pitch-shift')!.value = '';
  // Новый профиль — целиком локальный и бесплатный: и липсинк, и озвучка, и
  // распознавание. Платные облака включаются осознанно.
  $<HTMLSelectElement>('admin-adlg-tts-provider')!.value = 'silero';
  $<HTMLInputElement>('admin-adlg-silero-api-voice')!.value = '';
  $<HTMLInputElement>('admin-adlg-silero-api-emotion')!.value = '';
  $<HTMLSelectElement>('admin-adlg-stt-provider')!.value = 'browser';
  $<HTMLInputElement>('admin-adlg-stt-enhance')!.checked = false;
  syncVoiceFieldsToProvider();
  $<HTMLInputElement>('admin-adlg-idle-clips-dir')!.value = '';
  $<HTMLInputElement>('admin-adlg-clip-file')!.value = '';
  renderClipList([]);
  $('btn-admin-adlg-delete')?.classList.add('hidden');
}

function updateSpeechRateLabel(): void {
  const val = $<HTMLInputElement>('admin-adlg-speech-rate')?.value || '1';
  const label = $('admin-adlg-speech-rate-value');
  if (label) label.textContent = Number(val).toFixed(2);
}

/**
 * Показывает ровно то поле голоса, которое принадлежит выбранному провайдеру
 * озвучки. Значения всех трёх хранятся в профиле независимо (у каждого
 * провайдера свой словарь дикторов), поэтому переключение туда-обратно ничего
 * не теряет — прячется только то, что к делу не относится. Раньше все поля
 * висели одновременно, и по форме было не прочитать, каким голосом
 * заговорит аватар.
 */
function syncVoiceFieldsToProvider(): void {
  const provider = $<HTMLSelectElement>('admin-adlg-tts-provider')?.value || 'silero';
  const toggle = (id: string, visible: boolean): void => {
    $(id)?.classList.toggle('hidden', !visible);
  };
  toggle('admin-adlg-voice-silero', provider === 'silero');
  toggle('admin-adlg-voice-yandex', provider === 'yandex');
  toggle('admin-adlg-voice-silero-api', provider === 'silero_api');
  toggle('admin-adlg-emotion-silero-api', provider === 'silero_api');
}

async function openEditor(avatarId: string | null): Promise<void> {
  resetEditor();
  editingAvatarId = avatarId;

  if (avatarId) {
    let profile = profilesCache.find((p) => p.avatar_id === avatarId);
    if (!profile) {
      profile = await fetchJson<AvatarProfile>(`/api/admin/avg-dialog/profiles/${encodeURIComponent(avatarId)}`, {
        headers: authHeaders(),
      }) || undefined;
    }
    if (!profile) {
      showToast('❌ Аватар не найден');
      return;
    }

    $<HTMLInputElement>('admin-adlg-avatar-id')!.value = profile.avatar_id;
    $<HTMLInputElement>('admin-adlg-avatar-id')!.disabled = true;
    $<HTMLInputElement>('admin-adlg-label')!.value = profile.label || '';
    $<HTMLInputElement>('admin-adlg-sort-index')!.value = String(profile.sort_index ?? 0);
    populateCategorySelect(profile.category || '');
    populateSileroSpeakerSelect(profile.silero_speaker || '');
    $<HTMLSelectElement>('admin-adlg-sound-profile')!.value = profile.sound_profile || 'neutral';
    $<HTMLInputElement>('admin-adlg-speech-rate')!.value = String(profile.speech_rate ?? 1);
    updateSpeechRateLabel();
    $<HTMLInputElement>('admin-adlg-profile-video-path')!.value = profile.profile_video_path || '';
    setProfileVideoPreview(mediaUrl(profile.profile_video_path));
    $<HTMLInputElement>('admin-adlg-splash-path')!.value = profile.splash_image_path || '';
    setSplashPreview(mediaUrl(profile.splash_image_path));
    $<HTMLSelectElement>('admin-adlg-video-resolution')!.value = profile.video_resolution || '480p';
    // Тот же приоритет, что на сервере (resolveAvgDialogCaptionsMode): profile
    // без явного captions_mode мапится из легаси captions_enabled.
    $<HTMLSelectElement>('admin-adlg-captions-mode')!.value =
      profile.captions_mode || (profile.captions_enabled === false ? 'off' : 'immediate');
    $<HTMLTextAreaElement>('admin-adlg-greeting')!.value = profile.greeting || '';
    $<HTMLInputElement>('admin-adlg-passport-id')!.value = profile.passport_id || '';
    $<HTMLInputElement>('admin-adlg-passport-version')!.value = profile.passport_version || '';
    $<HTMLInputElement>('admin-adlg-method-frame-id')!.value = profile.method_frame_id || '';
    $<HTMLInputElement>('admin-adlg-method-frame-version')!.value =
      profile.method_frame_version != null ? String(profile.method_frame_version) : '';
    $<HTMLInputElement>('admin-adlg-output-frame-id')!.value = profile.output_frame_id || '';
    $<HTMLInputElement>('admin-adlg-output-frame-version')!.value =
      profile.output_frame_version != null ? String(profile.output_frame_version) : '';
    $<HTMLSelectElement>('admin-adlg-local-video-policy')!.value = profile.local_video_policy || 'video_first';
    $<HTMLInputElement>('admin-adlg-video-ready-timeout')!.value = profile.video_ready_timeout_ms != null ? String(profile.video_ready_timeout_ms) : '';
    $<HTMLInputElement>('admin-adlg-barge-in-enabled')!.checked = !!profile.barge_in_enabled;
    $<HTMLSelectElement>('admin-adlg-answer-length')!.value = profile.answer_length || 'medium';
    $<HTMLSelectElement>('admin-adlg-lipsync-engine')!.value = profile.lipsync_engine || 'wav2lip';
    populateYandexVoiceSelect(profile.yandex_voice || '');
    syncYandexRoleOptions(profile.yandex_role || '');
    $<HTMLInputElement>('admin-adlg-yandex-pitch-shift')!.value =
      profile.yandex_pitch_shift != null ? String(profile.yandex_pitch_shift) : '';
    $<HTMLSelectElement>('admin-adlg-tts-provider')!.value = profile.tts_provider || 'silero';
    $<HTMLInputElement>('admin-adlg-silero-api-voice')!.value = profile.silero_api_voice || '';
    $<HTMLInputElement>('admin-adlg-silero-api-emotion')!.value = profile.silero_api_emotion || '';
    // Пусто = наследовать глобальную настройку Диалога. Раньше здесь стоял
    // 'browser', и первое же сохранение профиля (ради голоса или заставки)
    // молча выключало серверный фоллбек распознавания.
    $<HTMLSelectElement>('admin-adlg-stt-provider')!.value = profile.stt_provider || '';
    $<HTMLInputElement>('admin-adlg-stt-enhance')!.checked = profile.stt_enhance === true;
    syncVoiceFieldsToProvider();
    $<HTMLInputElement>('admin-adlg-idle-clips-dir')!.value = profile.idle_clips_dir || '';
    await refreshClipList(profile.avatar_id);

    renderPersonalityState(profile.avatar_id);

    const title = $('admin-adlg-editor-title');
    if (title) title.textContent = 'Аватар: ' + (profile.label || profile.avatar_id);
    $('btn-admin-adlg-delete')?.classList.remove('hidden');
  } else {
    renderPersonalityState(null);
    const title = $('admin-adlg-editor-title');
    if (title) title.textContent = 'Новый аватар';
  }

  showEditor(true);
}

function readNumberOrUndefined(id: string): number | undefined {
  const raw = $<HTMLInputElement>(id)?.value.trim();
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function readEditorPayload(): AvatarProfile {
  return {
    avatar_id: $<HTMLInputElement>('admin-adlg-avatar-id')?.value.trim() || '',
    label: $<HTMLInputElement>('admin-adlg-label')?.value.trim() || undefined,
    sort_index: readNumberOrUndefined('admin-adlg-sort-index') ?? 0,
    category: $<HTMLSelectElement>('admin-adlg-category')?.value || null,
    silero_speaker: ($<HTMLSelectElement>('admin-adlg-speaker')?.value || SILERO_DEFAULT_SPEAKER) as SileroSpeaker,
    sound_profile: ($<HTMLSelectElement>('admin-adlg-sound-profile')?.value || 'neutral') as SoundProfile,
    speech_rate: Number($<HTMLInputElement>('admin-adlg-speech-rate')?.value || '1'),
    profile_video_path: $<HTMLInputElement>('admin-adlg-profile-video-path')?.value.trim() || null,
    splash_image_path: $<HTMLInputElement>('admin-adlg-splash-path')?.value.trim() || null,
    video_resolution: ($<HTMLSelectElement>('admin-adlg-video-resolution')?.value || '480p') as VideoResolution,
    captions_mode: ($<HTMLSelectElement>('admin-adlg-captions-mode')?.value || 'immediate') as CaptionsMode,
    greeting: $<HTMLTextAreaElement>('admin-adlg-greeting')?.value.trim() || null,
    // Пустое поле = снять привязку. Пара «id + версия» пишется целиком: полупустая
    // привязка отсекается CHECK-ом таблицы, и лучше увидеть ошибку в админке,
    // чем получить Аватара с паспортом без версии.
    passport_id: $<HTMLInputElement>('admin-adlg-passport-id')?.value.trim() || null,
    passport_version: $<HTMLInputElement>('admin-adlg-passport-version')?.value.trim() || null,
    method_frame_id: $<HTMLInputElement>('admin-adlg-method-frame-id')?.value.trim() || null,
    method_frame_version: readNumberOrUndefined('admin-adlg-method-frame-version') ?? null,
    output_frame_id: $<HTMLInputElement>('admin-adlg-output-frame-id')?.value.trim() || null,
    output_frame_version: readNumberOrUndefined('admin-adlg-output-frame-version') ?? null,
    local_video_policy: ($<HTMLSelectElement>('admin-adlg-local-video-policy')?.value || 'video_first') as LocalVideoPolicy,
    video_ready_timeout_ms: readNumberOrUndefined('admin-adlg-video-ready-timeout') ?? null,
    barge_in_enabled: $<HTMLInputElement>('admin-adlg-barge-in-enabled')?.checked === true,
    answer_length: ($<HTMLSelectElement>('admin-adlg-answer-length')?.value || 'medium') as AnswerLength,
    lipsync_engine: ($<HTMLSelectElement>('admin-adlg-lipsync-engine')?.value || 'wav2lip') as AvatarProfile['lipsync_engine'],
    yandex_voice: $<HTMLSelectElement>('admin-adlg-yandex-voice')?.value || null,
    yandex_role: $<HTMLSelectElement>('admin-adlg-yandex-role')?.value || null,
    yandex_pitch_shift: readNumberOrUndefined('admin-adlg-yandex-pitch-shift') ?? null,
    tts_provider: ($<HTMLSelectElement>('admin-adlg-tts-provider')?.value || 'silero') as AvatarProfile['tts_provider'],
    silero_api_voice: $<HTMLInputElement>('admin-adlg-silero-api-voice')?.value.trim() || null,
    silero_api_emotion: $<HTMLInputElement>('admin-adlg-silero-api-emotion')?.value.trim() || null,
    // null (пустое значение селекта) = профиль не задаёт распознавание и берёт
    // глобальную настройку; 'browser' здесь означал бы «без фоллбека вообще».
    stt_provider: ($<HTMLSelectElement>('admin-adlg-stt-provider')?.value || null) as AvatarProfile['stt_provider'],
    stt_enhance: $<HTMLInputElement>('admin-adlg-stt-enhance')?.checked === true,
    idle_clips_dir: $<HTMLInputElement>('admin-adlg-idle-clips-dir')?.value.trim() || null,
  };
}

async function saveEditor(): Promise<void> {
  const payload = readEditorPayload();
  if (!payload.avatar_id) {
    showToast('❌ Укажите ID аватара (avatar_id)');
    return;
  }

  const r = await fetch(`/api/admin/avg-dialog/profiles/${encodeURIComponent(payload.avatar_id)}`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(payload),
  });

  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { detail?: string; error?: { message?: string } };
    showToast('❌ ' + (err.detail || err.error?.message || 'Ошибка сохранения аватара'));
    return;
  }

  showToast('✅ Аватар сохранён');
  showEditor(false);
  await loadAdminAvgDialog();
}

async function deleteProfile(avatarId: string): Promise<void> {
  if (!avatarId) return;
  if (!confirm(`Удалить профиль «${avatarId}»?`)) return;

  const r = await fetch(`/api/admin/avg-dialog/profiles/${encodeURIComponent(avatarId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!r.ok) {
    // Причина отказа осмысленна (например, «это профиль по умолчанию») —
    // общий текст заставлял бы гадать, что именно не так.
    const err = await r.json().catch(() => ({})) as { detail?: string };
    showToast('❌ ' + (err.detail || 'Не удалось удалить аватар'));
    return;
  }

  showToast('✅ Аватар удалён');
  if (editingAvatarId === avatarId) showEditor(false);
  await loadAdminAvgDialog();
}

/** Лимит видео профиля — держим в паре с MAX_PROFILE_VIDEO_BYTES на сервере. */
const MAX_PROFILE_VIDEO_MB = 100;

async function handleProfileVideoChange(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  const avatarId = $<HTMLInputElement>('admin-adlg-avatar-id')?.value.trim();
  if (!avatarId) {
    showToast('❌ Сначала укажите ID аватара (avatar_id)');
    input.value = '';
    return;
  }
  // Отсекаем перебор до отправки: заливать 150 МБ ради 400-го ответа незачем.
  if (file.size > MAX_PROFILE_VIDEO_MB * 1024 * 1024) {
    showToast(`❌ Видео больше ${MAX_PROFILE_VIDEO_MB} МБ`);
    input.value = '';
    return;
  }

  try {
    showToast('⏳ Загрузка видео аватара…');
    // Сырое бинарное тело (см. handleClipFileChange): base64-JSON упирался в
    // глобальный лимит express.json (2mb).
    const r = await fetch(`/api/admin/avg-dialog/profile-video/${encodeURIComponent(avatarId)}`, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': file.type || 'application/octet-stream',
        'X-Filename': encodeURIComponent(file.name),
      },
      body: file,
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { detail?: string };
      throw new Error(err.detail || `Не удалось загрузить видео (HTTP ${r.status})`);
    }
    const data = await r.json() as { profile_video_path: string; url: string };

    $<HTMLInputElement>('admin-adlg-profile-video-path')!.value = data.profile_video_path;
    setProfileVideoPreview(data.url || mediaUrl(data.profile_video_path));
    showToast('✅ Видео аватара загружено');
  } catch (err) {
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  } finally {
    input.value = '';
  }
}

// ---- Заставка (статичная подложка окна видео Диалога) ----

const MAX_SPLASH_MB = 10;

/** Показывает/прячет предпросмотр заставки и кнопку «Убрать». */
function setSplashPreview(url: string): void {
  const preview = $<HTMLImageElement>('admin-adlg-splash-preview');
  const clearBtn = $('btn-admin-adlg-splash-clear');
  if (preview) {
    if (url) {
      preview.src = url;
      preview.classList.remove('hidden');
    } else {
      preview.removeAttribute('src');
      preview.classList.add('hidden');
    }
  }
  clearBtn?.classList.toggle('hidden', !url);
}

async function handleSplashFileChange(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  const avatarId = $<HTMLInputElement>('admin-adlg-avatar-id')?.value.trim();
  if (!avatarId) {
    showToast('❌ Сначала укажите ID аватара (avatar_id)');
    input.value = '';
    return;
  }
  if (file.size > MAX_SPLASH_MB * 1024 * 1024) {
    showToast(`❌ Изображение больше ${MAX_SPLASH_MB} МБ`);
    input.value = '';
    return;
  }

  try {
    showToast('⏳ Загрузка заставки…');
    const r = await fetch(`/api/admin/avg-dialog/splash-image/${encodeURIComponent(avatarId)}`, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': file.type || 'application/octet-stream',
        'X-Filename': encodeURIComponent(file.name),
      },
      body: file,
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { detail?: string };
      throw new Error(err.detail || `Не удалось загрузить заставку (HTTP ${r.status})`);
    }
    const data = await r.json() as { splash_image_path: string; url: string };

    $<HTMLInputElement>('admin-adlg-splash-path')!.value = data.splash_image_path;
    setSplashPreview(data.url || mediaUrl(data.splash_image_path));
    showToast('✅ Заставка загружена');
  } catch (err) {
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  } finally {
    input.value = '';
  }
}

/** «Убрать» — снимает заставку с профиля и удаляет файл. Для ещё не
 * сохранённого профиля (DELETE вернёт 404) достаточно очистить поле формы. */
async function clearSplash(): Promise<void> {
  const avatarId = $<HTMLInputElement>('admin-adlg-avatar-id')?.value.trim();
  if (!confirm('Убрать заставку?')) return;

  if (editingAvatarId && avatarId) {
    const r = await fetch(`/api/admin/avg-dialog/splash-image/${encodeURIComponent(avatarId)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!r.ok && r.status !== 404) {
      showToast('❌ Не удалось убрать заставку');
      return;
    }
  }

  $<HTMLInputElement>('admin-adlg-splash-path')!.value = '';
  setSplashPreview('');
  showToast('✅ Заставка убрана');
}

/** «Убрать» — снимает видео с профиля и удаляет файл. Для ещё не сохранённого
 * профиля (DELETE вернёт 404) достаточно очистить поле формы. */
async function clearProfileVideo(): Promise<void> {
  const avatarId = $<HTMLInputElement>('admin-adlg-avatar-id')?.value.trim();
  if (!confirm('Убрать видео аватара?')) return;

  if (editingAvatarId && avatarId) {
    const r = await fetch(`/api/admin/avg-dialog/profile-video/${encodeURIComponent(avatarId)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!r.ok && r.status !== 404) {
      showToast('❌ Не удалось убрать видео аватара');
      return;
    }
  }

  $<HTMLInputElement>('admin-adlg-profile-video-path')!.value = '';
  setProfileVideoPreview('');
  showToast('✅ Видео аватара убрано');
}

// ---- Idle clips (persona video clips, random per turn) ----

type ClipInfo = { filename: string; url: string; bytes: number };

function renderClipList(clips: ClipInfo[]): void {
  const list = $('admin-adlg-clip-list');
  if (!list) return;
  list.textContent = '';
  if (!clips.length) {
    list.innerHTML = '<div class="admin-adlg-hint">Клипы не загружены — используется статичное фото.</div>';
    return;
  }
  clips.forEach((clip) => {
    const el = document.createElement('div');
    el.className = 'user-item';
    const mb = (clip.bytes / (1024 * 1024)).toFixed(1);
    el.innerHTML = `
      <div class="user-item-info">
        <span class="user-item-name">${escapeHtml(clip.filename)}</span>
        <span class="user-item-cat">${escapeHtml(mb)} MB · <a href="${escapeHtml(clip.url)}" target="_blank" rel="noopener">просмотр</a></span>
      </div>
      <div class="user-item-actions">
        <button class="nav-btn btn-action-sm admin-adlg-clip-delete-btn" data-filename="${escapeHtml(clip.filename)}" aria-label="Удалить клип">🗑️</button>
      </div>
    `;
    list.appendChild(el);
  });
  list.querySelectorAll<HTMLButtonElement>('.admin-adlg-clip-delete-btn').forEach((btn) => {
    btn.addEventListener('click', () => void deleteClip(btn.dataset.filename || ''));
  });
}

async function refreshClipList(avatarId: string): Promise<void> {
  if (!avatarId) {
    renderClipList([]);
    return;
  }
  const data = await fetchJson<{ idle_clips_dir: string; clips: ClipInfo[] }>(
    `/api/admin/avg-dialog/idle-clips/${encodeURIComponent(avatarId)}`,
    { headers: authHeaders() }
  );
  renderClipList(data?.clips || []);
}

async function handleClipFileChange(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  const avatarId = $<HTMLInputElement>('admin-adlg-avatar-id')?.value.trim();
  if (!avatarId) {
    showToast('❌ Сначала укажите ID аватара (avatar_id)');
    input.value = '';
    return;
  }

  try {
    // Сырое бинарное тело: base64-JSON упирался в лимит express.json (2mb)
    // и резал любой клип крупнее ~1.4MB.
    const r = await fetch(`/api/admin/avg-dialog/idle-clips/${encodeURIComponent(avatarId)}`, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': file.type || 'application/octet-stream',
        'X-Filename': encodeURIComponent(file.name),
      },
      body: file,
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { detail?: string };
      throw new Error(err.detail || `Не удалось загрузить клип (HTTP ${r.status})`);
    }
    const data = await r.json() as { idle_clips_dir: string };
    $<HTMLInputElement>('admin-adlg-idle-clips-dir')!.value = data.idle_clips_dir;
    await refreshClipList(avatarId);
    showToast('✅ Клип загружен');
  } catch (err) {
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  } finally {
    input.value = '';
  }
}

async function deleteClip(filename: string): Promise<void> {
  const avatarId = $<HTMLInputElement>('admin-adlg-avatar-id')?.value.trim();
  if (!avatarId || !filename) return;
  if (!confirm(`Удалить клип «${filename}»?`)) return;

  const r = await fetch(
    `/api/admin/avg-dialog/idle-clips/${encodeURIComponent(avatarId)}/${encodeURIComponent(filename)}`,
    { method: 'DELETE', headers: authHeaders() }
  );
  if (!r.ok) {
    showToast('❌ Не удалось удалить клип');
    return;
  }
  await refreshClipList(avatarId);
  showToast('✅ Клип удалён');
}

async function loadDialogSettings(): Promise<void> {
  const settings = await fetchJson<DialogSettings>('/api/admin/avg-dialog/settings', { headers: authHeaders() });
  if (!settings) return;

  const defaultSel = $<HTMLSelectElement>('admin-adlg-settings-default-avatar');
  if (defaultSel) defaultSel.value = settings.default_avatar_id || '';
  const modeSel = $<HTMLSelectElement>('admin-adlg-settings-mode');
  if (modeSel) modeSel.value = settings.mode || 'live';
  const maxTurns = $<HTMLInputElement>('admin-adlg-settings-max-turns');
  if (maxTurns) maxTurns.value = String(settings.max_turns ?? 30);
  const timeoutMin = $<HTMLInputElement>('admin-adlg-settings-timeout-min');
  if (timeoutMin) timeoutMin.value = String(settings.timeout_min ?? 15);
  const voiceBargeIn = $<HTMLInputElement>('admin-adlg-settings-voice-barge-in-enabled');
  if (voiceBargeIn) voiceBargeIn.checked = !!settings.voice_barge_in_enabled;
  const historyMessages = $<HTMLInputElement>('admin-adlg-settings-history-messages');
  if (historyMessages) historyMessages.value = String(settings.history_messages ?? 20);
  const historySummary = $<HTMLInputElement>('admin-adlg-settings-history-summary');
  if (historySummary) historySummary.checked = settings.history_summary_enabled === true;
  const ttsAccents = $<HTMLTextAreaElement>('admin-adlg-settings-tts-accents');
  if (ttsAccents) ttsAccents.value = settings.tts_accents || '';
}

async function saveDialogSettings(): Promise<void> {
  const payload: DialogSettings = {
    default_avatar_id: $<HTMLSelectElement>('admin-adlg-settings-default-avatar')?.value || undefined,
    mode: ($<HTMLSelectElement>('admin-adlg-settings-mode')?.value || 'live') as DialogMode,
    max_turns: readNumberOrUndefined('admin-adlg-settings-max-turns'),
    timeout_min: readNumberOrUndefined('admin-adlg-settings-timeout-min'),
    // stt_provider / stt_enhance намеренно НЕ шлём: распознавание задаётся в
    // профиле аватара (миграция avg-dialog/009). Поля остаются в БД запасным
    // значением для профилей, где они не заполнены, — merge в saveSettings
    // сохранит их нетронутыми.
    // barge_in_enabled намеренно НЕ шлём: перебивание задаётся в профиле аватара
    // (колонка NOT NULL, профиль всегда перекрывал глобальную настройку). Поле
    // остаётся в БД как запасное значение для вызовов без профиля — merge в
    // saveSettings сохранит его нетронутым.
    voice_barge_in_enabled: $<HTMLInputElement>('admin-adlg-settings-voice-barge-in-enabled')?.checked === true,
    history_messages: readNumberOrUndefined('admin-adlg-settings-history-messages'),
    history_summary_enabled: $<HTMLInputElement>('admin-adlg-settings-history-summary')?.checked === true,
    tts_accents: $<HTMLTextAreaElement>('admin-adlg-settings-tts-accents')?.value ?? '',
  };

  const r = await fetch('/api/admin/avg-dialog/settings', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(payload),
  });

  if (!r.ok) {
    showToast('❌ Не удалось сохранить глобальные настройки');
    return;
  }

  showToast('✅ Глобальные настройки Диалога сохранены');
}

function bindEvents(): void {
  if (eventsBound) return;
  eventsBound = true;

  $('btn-admin-adlg-new')?.addEventListener('click', () => void openEditor(null));
  $('btn-admin-adlg-cancel')?.addEventListener('click', () => showEditor(false));
  $('btn-admin-adlg-save')?.addEventListener('click', () => void saveEditor());
  $('btn-admin-adlg-voice-preview')?.addEventListener('click', () => void previewVoice());
  $('btn-admin-adlg-delete')?.addEventListener('click', () => {
    if (editingAvatarId) void deleteProfile(editingAvatarId);
  });
  $('admin-adlg-speech-rate')?.addEventListener('input', updateSpeechRateLabel);
  $('admin-adlg-tts-provider')?.addEventListener('change', syncVoiceFieldsToProvider);
  // Смена голоса Yandex меняет доступный набор амплуа.
  $('admin-adlg-yandex-voice')?.addEventListener('change', () => syncYandexRoleOptions());
  $('admin-adlg-profile-video-file')?.addEventListener('change', (e) => void handleProfileVideoChange(e));
  $('btn-admin-adlg-profile-video-clear')?.addEventListener('click', () => void clearProfileVideo());
  $('admin-adlg-splash-file')?.addEventListener('change', (e) => void handleSplashFileChange(e));
  $('btn-admin-adlg-splash-clear')?.addEventListener('click', () => void clearSplash());
  $('admin-adlg-clip-file')?.addEventListener('change', (e) => void handleClipFileChange(e));
  $('btn-admin-adlg-settings-save')?.addEventListener('click', () => void saveDialogSettings());

  $('btn-admin-adlg-goto-categories')?.addEventListener('click', () => {
    const tab = document.querySelector<HTMLElement>('.admin-tab[data-tab="categories"]');
    tab?.click();
  });
}

export async function loadAdminAvgDialog(): Promise<void> {
  bindEvents();

  const list = $('admin-adlg-profile-list');
  if (list) list.innerHTML = '<div class="audit-log-empty">Загрузка...</div>';

  try {
    const [profiles, categories, , personality, refs] = await Promise.all([
      fetchJson<AvatarProfile[]>('/api/admin/avg-dialog/profiles', { headers: authHeaders() }),
      fetchAdminCategories(),
      // Каталог дикторов сайдкара: форма профиля должна показывать реально
      // загруженные голоса, а не зашитый в вёрстку список.
      loadSileroVoices(),
      // Личность каталога: состояние резолва по каждой записи и то, что вообще
      // можно привязать. Обе ручки необязательны для работы формы — при ошибке
      // остаётся прежний вид без подсказок.
      fetchJson<{ items: PersonalityStatus[] }>('/api/admin/avg-dialog/personality', { headers: authHeaders() }),
      fetchJson<PersonalityRefs>('/api/admin/avg-dialog/passports', { headers: authHeaders() }),
    ]);

    profilesCache = profiles || [];
    categoriesCache = categories;
    personalityCache = personality?.items || [];
    personalityRefsCache = refs || { passports: [], method_frames: [], output_frames: [] };

    renderProfileList();
    populateCategorySelect();
    renderPersonalityRefs();
    await loadDialogSettings();
  } catch (err) {
    if (list) list.innerHTML = '<div class="audit-log-empty">Ошибка загрузки.</div>';
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  }
}

export function bindAdminAvgDialogEvents(): void {
  bindEvents();
}
